import React, { useEffect, useState } from 'react'
import Button from '../../../../../components/ui/buttons/Button'
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf'
import { snakecaseKeys } from '../../../../../utils/snakecaseKeys'
import { camelizeKeys } from '../../../../../utils/camelizeKeys'
import CustomCheckbox from '../../../../../components/ui/checkboxes/CustomCheckbox'
import CustomRadio from '../../../../../components/ui/radios/CustomRadio'
import { useToast } from '../../../../../components/ui/toast/ToastContext'

const SuitabilityTab = ({ clientId, onSuccess, canWrite }) => {
  const [questions, setQuestions] = useState([])
  const [selectedAnswers, setSelectedAnswers] = useState([])
  const [loading, setLoading] = useState(false)
  const { addToast } = useToast()

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/profile/suitability`)
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        
        setQuestions(camelData.questions)
        setSelectedAnswers(camelData.selectedAnswerIds || [])
      })
  }

  useEffect(() => {
    fetchData()
  }, [clientId])

  const handleSelect = (questionId, answerId, multiple) => {
    setSelectedAnswers(prev => {
      const answerIdsFromQuestion = questions.find(q => q.id === questionId)?.answers.map(a => a.id) || []
      if (multiple) {
        return prev.includes(answerId)
          ? prev.filter(id => id !== answerId)
          : [...prev, answerId]
      } else {
        const cleared = prev.filter(id => !answerIdsFromQuestion.includes(id))
        return [...cleared, answerId]
      }
    })
  }

  const handleSubmit = async () => {
    setLoading(true)

    const suitabilityAnswerIds = {}
    questions.forEach((question) => {
      const selected = question.answers.map(a => a.id).filter(id => selectedAnswers.includes(id))
      if (selected.length > 0) suitabilityAnswerIds[question.id] = selected
    })

    const payload = snakecaseKeys({ suitability_answer_ids: suitabilityAnswerIds })

    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/profile/suitability`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      if (!res.ok) throw new Error((await res.json())?.message || 'Erro ao salvar respostas.')

      addToast({ type: 'success', message: 'Respostas salvas com sucesso!' })
      fetchData()
      onSuccess()
    } catch (err) {
      console.error('Erro:', err)
      addToast({ type: 'error', message: err.message || 'Erro ao salvar respostas.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {questions.map((question, index) => (
          <div key={question.id} className="bg-white rounded-xl p-6">
            <h3 className="text-base font-semibold text-blueDarker mb-4">
              {index + 1} - {question.name} {question.required && '*'}
            </h3>
            <div className="space-y-2">
              {question.answers.map((answer, i) => {
                const isSelected = selectedAnswers.includes(answer.id)
                const letter = String.fromCharCode(65 + i)
                const labelText = (
                  <>
                    <span className="font-semibold text-base">{letter} - </span>{answer.label}
                  </>
                )

                return question.multiple ? (
                  <CustomCheckbox
                    key={answer.id}
                    name={`question-${question.id}`}
                    checked={isSelected}
                    onChange={() => handleSelect(question.id, answer.id, true)}
                    label={labelText}
                    labelClassName="text-base"
                    disabled={!canWrite}
                  />
                ) : (
                  <CustomRadio
                    key={answer.id}
                    name={`question-${question.id}`}
                    checked={isSelected}
                    onChange={() => handleSelect(question.id, answer.id, false)}
                    label={labelText}
                    labelClassName="text-base"
                    disabled={!canWrite}
                  />
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {canWrite && (
        <div className="flex justify-end mt-8">
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Salvando...' : 'SALVAR E CONTINUAR'}
          </Button>
        </div>
      )}
    </>
  )
}

export default SuitabilityTab
